// frontend/src/components/Users.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import api from '../services/api';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [username, setUsername] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('user');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data);
      setError('');
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (user = null) => {
    if (user) {
      setUsername(user.username);
      setPhone(user.phone || '');
      setPassword('');
      setRole(user.role);
      setIsEditing(true);
      setEditingUserId(user._id);
    } else {
      setUsername('');
      setPhone('');
      setPassword('');
      setRole('user');
      setIsEditing(false);
      setEditingUserId(null);
    }
    setError('');
    setSuccess('');
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setUsername('');
    setPhone('');
    setPassword('');
    setRole('user');
    setIsEditing(false);
    setEditingUserId(null);
    setError('');
    setSuccess('');
  };

  const handleSubmitUser = async () => {
    // Validation
    if (!username.trim()) {
      setError('Username/Email is required');
      return;
    }
    
    if (!isEditing && !password.trim()) {
      setError('Password is required for new users');
      return;
    }
    
    if (!phone.trim()) {
      setError('Phone number is required');
      return;
    }

    // Phone validation (basic - 10 digits)
    const cleanPhone = phone.replace(/\D/g, '');
    if (cleanPhone.length !== 10) {
      setError('Please enter a valid 10-digit phone number');
      return;
    }

    setError('');
    setSuccess('');

    try {
      if (isEditing) {
        const updateData = { username, phone, role };
        if (password && password.trim() !== '') {
          updateData.password = password;
        }
        await api.put(`/users/${editingUserId}`, updateData);
        setSuccess('User updated successfully');
      } else {
        await api.post('/users', { username, phone, password, role });
        setSuccess('User created successfully');
      }
      
      // Wait a moment to show success message, then close and refresh
      setTimeout(() => {
        handleCloseDialog();
        fetchUsers();
      }, 1000);
    } catch (error) {
      console.error('Error submitting user:', error);
      
      // Extract error message from response
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.message || 
                          error.message || 
                          'Failed to save user';
      
      setError(errorMessage);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await api.delete(`/users/${userId}`);
        fetchUsers();
        setError('');
      } catch (error) {
        console.error('Error deleting user:', error);
        setError('Failed to delete user');
      }
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
              <Typography variant="h5" gutterBottom>
        Users
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}
      
      <Button
        variant="contained"
        color="primary"
        startIcon={<AddIcon />}
        onClick={() => handleOpenDialog()}
        sx={{ mb: 2 }}
      >
        Add New User
      </Button>
      
      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ bgcolor: 'primary.main' }}>
            <TableRow>
              <TableCell sx={{ color: '#fff' }}>Username/Email</TableCell>
              <TableCell sx={{ color: '#fff' }}>Phone</TableCell>
              <TableCell sx={{ color: '#fff' }}>Role</TableCell>
              <TableCell sx={{ color: '#fff' }}>Created At</TableCell>
              <TableCell sx={{ color: '#fff' }}>Updated At</TableCell>
              <TableCell sx={{ color: '#fff' }}>Updated By</TableCell>
              <TableCell align="right" sx={{ color: '#fff' }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => {
              // Format date with error handling
              const formatDate = (dateString) => {
                try {
                  if (!dateString) return 'N/A';
                  const date = new Date(dateString);
                  if (isNaN(date.getTime())) return 'N/A';
                  return date.toLocaleString('en-IN', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  });
                } catch (error) {
                  console.error('Date formatting error:', error);
                  return 'N/A';
                }
              };
              
              // Check if user was actually updated (has updatedBy or updatedAt is significantly different from createdAt)
              const wasUpdated = (() => {
                try {
                  // If updatedBy exists, user was definitely updated
                  if (user.updatedBy) return true;
                  
                  // Otherwise, check if updatedAt is significantly different from createdAt
                  if (!user.updatedAt || !user.createdAt) return false;
                  const updatedTime = new Date(user.updatedAt).getTime();
                  const createdTime = new Date(user.createdAt).getTime();
                  if (isNaN(updatedTime) || isNaN(createdTime)) return false;
                  // Consider updated if the difference is more than 5 seconds (to account for timing differences)
                  return Math.abs(updatedTime - createdTime) > 5000;
                } catch (error) {
                  return false;
                }
              })();
              
              // Check if updatedAt exists and is valid
              const updatedAtDate = (() => {
                try {
                  if (!user.updatedAt) return null;
                  const date = new Date(user.updatedAt);
                  if (isNaN(date.getTime())) return null;
                  return date;
                } catch (error) {
                  return null;
                }
              })();
              
              return (
                <TableRow key={user._id || user.id}>
                  <TableCell>{user.username}</TableCell>
                  <TableCell>{user.phone || 'N/A'}</TableCell>
                  <TableCell>{user.role}</TableCell>
                  <TableCell>{formatDate(user.createdAt)}</TableCell>
                  <TableCell>
                    {updatedAtDate ? (
                      <Tooltip title={wasUpdated ? `Last updated: ${formatDate(user.updatedAt)}` : `Created: ${formatDate(user.createdAt)}`} arrow>
                        <span>{formatDate(user.updatedAt)}</span>
                      </Tooltip>
                    ) : (
                      <span style={{ color: '#999' }}>Never</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {user.updatedBy ? (
                      <Tooltip title={`Updated by: ${user.updatedBy.username || 'Unknown'}`} arrow>
                        <span>{user.updatedBy.username || 'Unknown'}</span>
                      </Tooltip>
                    ) : (
                      <span style={{ color: '#999' }}>–</span>
                    )}
                  </TableCell>
                  <TableCell align="right">
                  <Tooltip title="Edit User">
                    <IconButton
                      color="secondary"
                      onClick={() => handleOpenDialog(user)}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete User">
                    <IconButton
                      color="error"
                      onClick={() => handleDeleteUser(user._id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
              );
            })}
            {users.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No users found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="sm">
        <DialogTitle>{isEditing ? 'Edit User' : 'Add New User'}</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}
          <Box component="form" sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Username/Email"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              margin="normal"
              required
              error={error.includes('Username') || error.includes('username')}
              helperText={
                error.includes('Username') || error.includes('username')
                  ? error 
                  : 'Enter username or email address'
              }
            />
            <TextField
              fullWidth
              label="Phone Number"
              value={phone}
              onChange={(e) => {
                // Allow only digits
                const value = e.target.value.replace(/\D/g, '');
                if (value.length <= 10) {
                  setPhone(value);
                }
              }}
              margin="normal"
              required
              inputProps={{ maxLength: 10 }}
              error={!phone.trim() && error.includes('Phone')}
              helperText={error.includes('phone') ? error : 'Enter 10-digit phone number'}
            />
            <TextField
              fullWidth
              label={isEditing ? 'Password (leave blank for no change)' : 'Password'}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              required={!isEditing}
              error={!password.trim() && !isEditing && error.includes('Password')}
            />
            <FormControl fullWidth margin="normal">
              <InputLabel id="role-label">Role</InputLabel>
              <Select
                labelId="role-label"
                value={role}
                label="Role"
                onChange={(e) => setRole(e.target.value)}
              >
                <MenuItem value="user">User</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleSubmitUser} color="primary">
            {isEditing ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Users;
